import axios from "axios";
import { API_ENDPOINT } from "../../../../../lib/api-url";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import TradeHistoryComponent from "./tradeHistoryComponent";
import TradeAnalysisComponent from "./tradeAnalysisComponent";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const TEAM_LOGOS = {
  alphawolves: "/teams/alphawolves-logo.webp",
  bullmasters: "/teams/bullmaster-logo.webp",
  ironbears: "/teams/ironbears-logo.webp",
  goldeneagles: "/teams/goldeneagles-logo.webp",
};

const normalizeTeamKey = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[-_]/g, "");

const formatCompact = (v) =>
  Math.abs(v) >= 1000
    ? Intl.NumberFormat("en", { notation: "compact" }).format(v)
    : Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(v);

const fmt2 = (n) =>
  Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    Number(n || 0)
  );

const fmtCompactUSDNoSymbol = (n) =>
  Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const toNumber = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const toChartData = (userData) => {
  const rows = userData?.equity_balance?.data ?? [];
  const years = new Set(
    rows
      .map((r) => r["on-date"])
      .map((s) => Number(s?.split(".")[2]) || null)
      .filter(Boolean)
  );
  const includeYear = years.size > 1;
  return rows.map((r) => {
    const [dd, mm, yy] = String(r["on-date"] || "").split(".");
    const day = Number(dd);
    const month = Number(mm) - 1;
    const fullYear = 2000 + Number(yy);
    const date = new Date(Date.UTC(fullYear, month, day));
    const monthShort = date.toLocaleString("en", { month: "short" });
    return {
      date,
      label: includeYear
        ? `${day} ${monthShort} ${String(fullYear).slice(-2)}`
        : `${day} ${monthShort}`,
      balance: Number(r.balance ?? 0),
      equity: Number(r.equity ?? 0),
    };
  });
};

const lastFinite = (arr, key, { notZero = false } = {}) => {
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = Number(arr[i]?.[key]);
    if (Number.isFinite(v) && (!notZero || v !== 0)) return v;
  }
  return null;
};

const pickEquity = (userData, chartData) => {
  const fromSeries = lastFinite(chartData, "equity", { notZero: true });
  if (fromSeries != null) return fromSeries;
  const v = Number(userData?.user?.equity);
  return Number.isFinite(v) ? v : 0;
};
const pickBalance = (userData, chartData) => {
  const fromSeries = lastFinite(chartData, "balance", { notZero: true });
  if (fromSeries != null) return fromSeries;
  const v = Number(userData?.user?.balance);
  return Number.isFinite(v) ? v : 0;
};

/* profit is derived from balance - initial balance; no longer from deals/metrics */
// (leaving old pickProfit unused on purpose to avoid UI/motion changes elsewhere)

/* ---------------- Count up ---------------- */

function useCountUp({
  to = 0,
  duration = 1800,
  delay = 0,
  ease = (t) => 1 - Math.pow(1 - t, 3),
  onComplete,
}) {
  const [val, setVal] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(null);
  const cancelledRef = useRef(false);
  const startedOnceRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    startRef.current = null;
    if (startedOnceRef.current) return () => {};
    startedOnceRef.current = true;
    const startAnim = () => {
      const step = (ts) => {
        if (cancelledRef.current) return;
        if (startRef.current == null) startRef.current = ts;
        const t = Math.min(1, (ts - startRef.current) / duration);
        const e = ease ? ease(t) : t;
        setVal(to * e);
        if (t < 1) rafRef.current = requestAnimationFrame(step);
        else {
          setVal(to);
          onComplete && onComplete();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    };
    const timer = setTimeout(startAnim, Math.max(0, delay));
    return () => {
      cancelledRef.current = true;
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startedOnceRef.current = false;
    };
  }, [to]);
  return val;
}

const CountTo = ({
  value,
  showCurrency = false,
  duration = 2000,
  delay = 0,
  onComplete,
}) => {
  const v = useCountUp({ to: value, duration, delay, onComplete });
  const txt = showCurrency
    ? Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(v)
    : formatCompact(v);
  return <span>{txt}</span>;
};

const EASE = [0.2, 0.8, 0.2, 1];
const VIEW_EASE = [0.2, 0.8, 0.2, 1];

const itemUp = {
  hidden: { opacity: 0, y: 18, filter: "blur(4px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 1.0, ease: EASE },
  },
};
const panelRise = {
  hidden: { opacity: 0, y: 20, scale: 0.985, filter: "blur(4px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 1.1, ease: EASE },
  },
};
const viewVariants = {
  initial: { opacity: 0, y: 8, filter: "blur(2px)" },
  enter: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.45, ease: VIEW_EASE },
  },
  exit: {
    opacity: 0,
    y: -10,
    filter: "blur(6px)",
    transition: { duration: 0.45, ease: VIEW_EASE },
  },
};

const EquityDot = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} r={9} fill="#7086FD" opacity={0.25} />
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="#7086FD"
      stroke="#FFFFFF"
      strokeWidth={1}
    />
  </g>
);
const BalanceDot = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} r={9} fill="#6FD195" opacity={0.25} />
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="#FD0D0E"
      stroke="#FFFFFF"
      strokeWidth={1}
    />
  </g>
);

function EquityBalanceChart({ data, animate = true }) {
  const TICK_STYLE = { fontSize: 12, fill: "#A2A6AD", fontWeight: 400 };
  return (
    <div
      style={{
        width: "100%",
        height: 320,
        background: "transparent",
        borderRadius: 12,
        padding: 12,
      }}
    >
      <ResponsiveContainer>
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
        >
          <defs>
            <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(112, 134, 253, 0.3)" />
              <stop offset="100%" stopColor="rgba(112, 134, 253, 0.05)" />
            </linearGradient>
            <linearGradient id="balanceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(253, 13, 14, 0.76)" />
              <stop offset="100%" stopColor="rgba(253, 13, 14, 0.05)" />
            </linearGradient>
          </defs>

          <CartesianGrid
            stroke="rgba(186,186,186,0.25)"
            strokeDasharray="2 2"
            vertical
            horizontal
          />
          <XAxis
            dataKey="date"
            tickFormatter={(_v, i) => data[i]?.label}
            tick={TICK_STYLE}
            axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
            tickLine={false}
            padding={{ left: 42, right: 42 }}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={TICK_STYLE}
            axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
            tickLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1 }}
            labelFormatter={(_v, i) => data[i]?.label}
            contentStyle={{
              background: "#0C0E23",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              color: "#A2A6AD",
              fontSize: 12,
            }}
            labelStyle={{ color: "#A2A6AD" }}
            formatter={(value, name) => [value, name]}
          />
          <Legend
            wrapperStyle={{ color: "#A2A6AD", fontSize: 12 }}
            iconType="circle"
          />

          <Area
            type="monotone"
            dataKey="equity"
            fill="url(#equityFill)"
            stroke="none"
            animationDuration={animate ? 2200 : 0}
            animationEasing="ease-in-out"
            dot={false}
            activeDot={false}
          />
          <Area
            type="monotone"
            dataKey="balance"
            fill="url(#balanceFill)"
            stroke="none"
            animationDuration={animate ? 2200 : 0}
            animationEasing="ease-in-out"
            dot={false}
            activeDot={false}
          />
          <Line
            type="monotone"
            dataKey="equity"
            name="Equity"
            stroke="#7086FD"
            strokeWidth={1}
            dot={<EquityDot />}
            activeDot={{ r: 7 }}
            animationDuration={animate ? 2600 : 0}
            animationEasing="ease-in-out"
          />
          <Line
            type="monotone"
            dataKey="balance"
            name="Balance"
            stroke="#FD0D0E"
            strokeWidth={1}
            dot={<BalanceDot />}
            activeDot={{ r: 7 }}
            animationDuration={animate ? 2600 : 0}
            animationEasing="ease-in-out"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ------------------ Skeletons ------------------ */

const Skel = ({ className, style }) => (
  <div
    className={`animate-pulse bg-[#14173A] rounded-[12px] ${className || ""}`}
    style={style}
  />
);
const CardSkeleton = () => <Skel className="w-[278px] h-[100px]" />;
const RowSkeleton = () => <Skel className="w-[300px] h-[37px]" />;

const ChartShell = ({ children }) => (
  <div
    className="chart w-full max-w-[1219px] rounded-[15px] p-4 mt-6"
    style={{ minHeight: 344 }}
  >
    {children}
  </div>
);

/* --------- Header card skeleton: only when loading; keep layout via spacer --------- */
function HeaderCardSlot({ showReal, loading, delay = 0, children }) {
  const renderSkeleton = loading;
  return (
    <div className="relative w-[278px] h-[100px]" style={{ flex: "0 0 auto" }}>
      {renderSkeleton ? (
        <CardSkeleton />
      ) : (
        <div className="w-[278px] h-[100px] invisible" aria-hidden />
      )}
      <AnimatePresence>
        {showReal && (
          <motion.div
            key="real"
            initial={{ opacity: 0, y: 18, filter: "blur(4px)" }}
            animate={{
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: { duration: 1.0, delay, ease: EASE },
            }}
            exit={{
              opacity: 0,
              y: -6,
              filter: "blur(6px)",
              transition: { duration: 0.4 },
            }}
            className="absolute inset-0"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ========================= PARENT ========================= */

export default function AnalysisPanelComponent() {
  const [hasPayload, setHasPayload] = useState(false);
  const [currentTarget, setCurrentTarget] = useState("group");
  const [userData, setUserData] = useState(null);
  const [dailyStart, setDailyStart] = useState(null);
  const [dailyThresholdPct, setDailyThresholdPct] = useState(null);
  const [dailyMaxAllowedLoss, setDailyMaxAllowedLoss] = useState(null);
  const [minOverallEquityAllowed, setMinOverallEquityAllowed] = useState(null);
  const [initialBalance, setInitialBalance] = useState(null);
  const [userName, setUserName] = useState("");
  const [headerImgSrc, setHeaderImgSrc] = useState(
    "/avatar/avatar-default.webp"
  );
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("loading"); // loading -> cards -> chart -> rows
  const [c1Done, setC1Done] = useState(false);
  const [c2Done, setC2Done] = useState(false);
  const [c3Done, setC3Done] = useState(false);
  const [view, setView] = useState("main"); // "main" | "history" | "analysis"

  const inflightRef = useRef(null);
  const bcRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    bcRef.current = new BroadcastChannel("operator-events");

    const onBC = (e) => {
      const { actionId, payload } = e?.data || {};
      if (actionId === "WALL_SET_ANALYSIS_TARGET")
        handleIncomingPayload(payload);
      if (actionId === "WALL_SHOW_TRADE_HISTORY") setView("history");
      if (actionId === "WALL_SHOW_TRADE_ANALYSIS") setView("analysis");
    };
    bcRef.current.addEventListener("message", onBC);

    const onWin = (ev) => {
      const payload = ev?.detail;
      if (ev.type === "WALL_SET_ANALYSIS_TARGET")
        handleIncomingPayload(payload);
      if (ev.type === "WALL_SHOW_TRADE_HISTORY") setView("history");
      if (ev.type === "WALL_SHOW_TRADE_ANALYSIS") setView("analysis");
    };
    window.addEventListener("WALL_SET_ANALYSIS_TARGET", onWin);
    window.addEventListener("WALL_SHOW_TRADE_HISTORY", onWin);
    window.addEventListener("WALL_SHOW_TRADE_ANALYSIS", onWin);

    return () => {
      try {
        bcRef.current?.removeEventListener("message", onBC);
        bcRef.current?.close();
      } catch {}
      window.removeEventListener("WALL_SET_ANALYSIS_TARGET", onWin);
      window.removeEventListener("WALL_SHOW_TRADE_HISTORY", onWin);
      window.removeEventListener("WALL_SHOW_TRADE_ANALYSIS", onWin);
    };
  }, []);

  const chartData = useMemo(() => toChartData(userData), [userData]);
  const targetEquity = useMemo(
    () => pickEquity(userData, chartData),
    [userData, chartData]
  );
  const targetBalance = useMemo(
    () => pickBalance(userData, chartData),
    [userData, chartData]
  );

  /* -------- Profit now = current balance - initial balance -------- */
  const targetProfit = useMemo(() => {
    const ib = toNumber(initialBalance);
    const cb = toNumber(targetBalance);
    if (!Number.isFinite(ib) || !Number.isFinite(cb)) return 0;
    return cb - ib;
  }, [initialBalance, targetBalance]);

  useEffect(() => {
    if (stage === "cards" && c1Done && c2Done && c3Done) {
      const t = setTimeout(() => setStage("chart"), 500);
      return () => clearTimeout(t);
    }
  }, [stage, c1Done, c2Done, c3Done]);

  useEffect(() => {
    if (stage === "chart") {
      const t = setTimeout(() => setStage("rows"), 2600);
      return () => clearTimeout(t);
    }
  }, [stage]);

  const cardsReal =
    hasPayload &&
    !loading &&
    (stage === "cards" || stage === "chart" || stage === "rows");
  const showCard1 = cardsReal;
  const showCard2 = cardsReal;
  const showCard3 = cardsReal;
  const showCard4 = cardsReal;

  const handleIncomingPayload = (payload) => {
    setView("main");

    const loginRaw = payload?.login;
    (() => {
      const targetRaw = payload?.target;
      const login = Number(loginRaw);
      if (!Number.isFinite(login) || login <= 0) return;

      const targetForMargin =
        targetRaw === "group"
          ? "groups"
          : targetRaw === "individual"
          ? "individuals"
          : "individuals";

      try {
        inflightRef.current?.abort();
      } catch {}
      const controller = new AbortController();
      inflightRef.current = controller;

      setHasPayload(true);
      setCurrentTarget({ login, target: targetRaw });
      setLoading(true);
      setStage("loading");
      setUserData(null);
      setC1Done(false);
      setC2Done(false);
      setC3Done(false);
      setDailyStart(null);
      setDailyThresholdPct(null);
      setDailyMaxAllowedLoss(null);
      setMinOverallEquityAllowed(null);
      setInitialBalance(null);
      setUserName("");
      setHeaderImgSrc("/avatar/avatar-default.webp");

      (async () => {
        let _dailyMaxLoss = null;
        let _ownerName = "";
        try {
          const pm = await axios.post(
            API_ENDPOINT.wallPositions.postMargin,
            { login: String(login), target: targetForMargin },
            { signal: controller.signal }
          );
          const md = pm?.data?.data?.data || {};
          const _dailyStart = toNumber(md.daily_starting_balance);
          const _dailyThreshold = toNumber(md.daily_threshold_percent);
          _dailyMaxLoss = toNumber(md.daily_max_allowed_loss);
          _ownerName = pm?.data?.owner_name || pm?.data?.team_name;

          setDailyStart(_dailyStart);
          setDailyThresholdPct(_dailyThreshold);
          setDailyMaxAllowedLoss(_dailyMaxLoss);

          /* initial balance now comes from API */
          const apiInitial = toNumber(md.initial_account_balance);
          setInitialBalance(apiInitial);

          if (targetRaw === "group") {
            const key = normalizeTeamKey(_ownerName);
            const logo = TEAM_LOGOS[key] || "/avatar/avatar-default.webp";
            setHeaderImgSrc(logo);
            setUserName(_ownerName || "");
          } else {
            setHeaderImgSrc("/avatar/avatar-default.webp");
            setUserName(_ownerName || "");
          }
        } catch {}

        try {
          const res = await axios.get(
            API_ENDPOINT.wallPositions.getEachUser(login),
            {
              signal: controller.signal,
            }
          );
          setUserData(res?.data);
          const equity = toNumber(res?.data?.user?.equity);
          if (_dailyMaxLoss != null) {
            const _minOverallEquity = Math.max(0, equity - _dailyMaxLoss);
            setMinOverallEquityAllowed(_minOverallEquity);
          }
        } catch {
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
            setStage("cards");
          }
        }
      })();
    })();
  };

  const safeDiv = (a, b) => {
    const x = Number(a);
    const y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) return 0;
    return (x / y) * 100;
  };

  const profitPct = useMemo(
    () => safeDiv(targetProfit, initialBalance),
    [targetProfit, initialBalance]
  );
  const equityPct = useMemo(
    () => safeDiv(targetEquity, initialBalance),
    [targetEquity, initialBalance]
  );
  const balancePct = useMemo(
    () => safeDiv(targetBalance, initialBalance),
    [targetBalance, initialBalance]
  );

  const rootKey = `${currentTarget?.login || "none"}`;

  /* ------------------ RENDER ------------------ */
  const chartReady = hasPayload && (stage === "chart" || stage === "rows");
  const rowsReady = hasPayload && stage === "rows";

  return (
    <AnimatePresence mode="wait" initial={false}>
      {view === "main" ? (
        <motion.div
          key={`main-${rootKey}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: EASE },
          }}
          exit={{
            opacity: 0,
            y: -10,
            filter: "blur(6px)",
            transition: { duration: 0.45, ease: EASE },
          }}
          className="w-full h-full flex items-center justify-center flex-col"
        >
          {/* Header cards */}
          <div className="panel-header-container w-full flex items-center justify-center gap-[13px]">
            <div className="flex gap-[13px]">
              <HeaderCardSlot
                showReal={showCard1}
                loading={loading}
                delay={0.0}
              >
                <motion.div
                  className="card-container bg-[#0C0E23] flex gap-[19px] rounded-[15px] items-center justify-center w-[278px] p-[17px] h-[100px]"
                  variants={itemUp}
                  initial="hidden"
                  animate="show"
                >
                  <div className="w-full flex flex-col items-center justify-center">
                    <div className="field-container w-full flex justify-between flex-row-reverse">
                      <span className="text-[#CECECE] text-[16px] font-bold">
                        سود
                      </span>
                      <div className="value rounded-[8px] bg-[#4FC86540] w-[66px] h-[23px] flex items-center justify-center">
                        <span className="text-[12px] text-white">
                          {fmt2(profitPct)}%
                        </span>
                      </div>
                    </div>
                    <div className="amount w-full text-right text-[#FFFFFF] text-[22px] font-bold">
                      <CountTo
                        value={targetProfit}
                        showCurrency
                        duration={2200}
                        delay={200}
                        onComplete={() => setC1Done(true)}
                      />
                    </div>
                  </div>
                </motion.div>
              </HeaderCardSlot>

              <HeaderCardSlot
                showReal={showCard2}
                loading={loading}
                delay={0.15}
              >
                <motion.div
                  className="card-container bg-[#0C0E23] flex gap-[19px] rounded-[15px] items-center justify-center w-[278px] p-[17px] h-[100px]"
                  variants={itemUp}
                  initial="hidden"
                  animate="show"
                >
                  <div className="w-full flex flex-col items-center justify-center">
                    <div className="field-container w-full flex justify-between flex-row-reverse">
                      <span className="text-[#CECECE] text-[16px] font-bold">
                        موجودی لحظه ای
                      </span>
                      <div className="value rounded-[8px] bg-[#4FC86540] w-[66px] h-[23px] flex items-center justify-center">
                        <span className="text-[12px] text-white">
                          {fmt2(equityPct)}%
                        </span>
                      </div>
                    </div>
                    <div className="amount w-full text-right text-[#FFFFFF] text-[22px] font-bold">
                      <CountTo
                        value={Math.max(0, targetEquity)}
                        showCurrency
                        duration={2300}
                        delay={350}
                        onComplete={() => setC2Done(true)}
                      />
                    </div>
                  </div>
                </motion.div>
              </HeaderCardSlot>

              <HeaderCardSlot
                showReal={showCard3}
                loading={loading}
                delay={0.3}
              >
                <motion.div
                  className="card-container bg-[#0C0E23] flex gap-[19px] rounded-[15px] items-center justify-center w-[278px] p-[17px] h-[100px]"
                  variants={itemUp}
                  initial="hidden"
                  animate="show"
                >
                  <div className="w-full flex flex-col items-center justify-center">
                    <div className="field-container w-full flex justify-between flex-row-reverse">
                      <span className="text-[#CECECE] text-[16px] font-bold">
                        موجودی
                      </span>
                      <div className="value rounded-[8px] bg-[#4FC86540] w-[66px] h-[23px] flex items-center justify-center">
                        <span className="text-[12px] text-white">
                          {fmt2(balancePct)}%
                        </span>
                      </div>
                    </div>
                    <div className="amount w-full text-right text-[#FFFFFF] text-[22px] font-bold">
                      <CountTo
                        value={Math.max(0, targetBalance)}
                        showCurrency
                        duration={2300}
                        delay={500}
                        onComplete={() => setC3Done(true)}
                      />
                    </div>
                  </div>
                </motion.div>
              </HeaderCardSlot>

              <HeaderCardSlot
                showReal={showCard4}
                loading={loading}
                delay={0.45}
              >
                <motion.div
                  style={{
                    background:
                      "linear-gradient(104.28deg, #0C0E23 47.48%, #0E1028 58.34%, #131738 68.22%, #2F3789 97.94%)",
                  }}
                  className="card-container flex gap-[19px] rounded-[15px] items-center justify-between w-[278px] p-[17px] h-[100px] pl-[32px]"
                  variants={itemUp}
                  initial="hidden"
                  animate="show"
                >
                  <div className="name-container">
                    <span
                      style={{
                        fontFamily:
                          currentTarget.target === "group" ? "Etemad" : "",
                        fontSize:
                          currentTarget.target === "group" ? "14px" : "20px",
                      }}
                      className="text-[#CECECE]  font-semibold"
                    >
                      {userName}
                    </span>
                  </div>
                  <div className="avatar-container">
                    <img
                      src={headerImgSrc}
                      className={
                        currentTarget.target === "group"
                          ? "w-[100px]"
                          : "w-[74px]"
                      }
                      alt=""
                    />
                  </div>
                </motion.div>
              </HeaderCardSlot>
            </div>
          </div>

          {/* CHART: skeleton only while loading; keep layout via ChartShell minHeight */}
          <ChartShell>
            <div style={{ position: "relative", width: "100%", height: 320 }}>
              {loading && !chartReady && (
                <Skel
                  style={{ width: "100%", height: "100%", borderRadius: 12 }}
                />
              )}
              {!loading && !chartReady && (
                <div
                  className="w-full h-full rounded-[12px] invisible"
                  aria-hidden
                />
              )}
              {chartReady && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.9, ease: EASE },
                  }}
                  style={{ width: "100%", height: "100%" }}
                >
                  <EquityBalanceChart data={toChartData(userData)} animate />
                </motion.div>
              )}
            </div>
          </ChartShell>

          {/* ROWS: mount only as skeleton during fetch; mount real content only at rows stage */}
          <AnimatePresence mode="wait" initial={false}>
            {loading && !rowsReady ? (
              <div
                key="rows-skel"
                style={{ padding: "16px 52px 21px 41px" }}
                className="flex items-start justify-center gap-[144px] w-[1119px] h-[206px] rounded-[15px] bg-[#0C0E23] mt-[16px]"
              >
                <div className="flex items-start justify-center gap-[70px] w-full h-full">
                  <div className="flex flex-col items-center gap-[9px]">
                    <Skel className="w-[160px] h-[20px]" />
                    <RowSkeleton />
                    <RowSkeleton />
                  </div>
                  <div className="flex flex-col items-center gap-[9px]">
                    <Skel className="w-[160px] h-[20px]" />
                    <RowSkeleton />
                    <RowSkeleton />
                    <RowSkeleton />
                  </div>
                  <div className="flex flex-col items-center gap-[9px]">
                    <Skel className="w-[180px] h-[20px]" />
                    <RowSkeleton />
                  </div>
                </div>
              </div>
            ) : null}

            {rowsReady ? (
              <motion.div
                key="rows-real"
                style={{ padding: "16px 52px 21px 41px" }}
                className="flex items-start justify-center gap-[74px] w-[1119px] h-[206px] rounded-[15px] bg-[#0C0E23] mt-[16px]"
                initial={{ opacity: 0, y: 12, filter: "blur(4px)" }}
                animate={{
                  opacity: 1,
                  y: 0,
                  filter: "blur(0px)",
                  transition: { duration: 0.8, ease: EASE },
                }}
                exit={{ opacity: 0, y: -8, transition: { duration: 0.3 } }}
              >
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: {
                      transition: { staggerChildren: 0.22, delayChildren: 0.1 },
                    },
                  }}
                  className="flex flex-col items-center justify-center gap-[9px]"
                >
                  <motion.span
                    variants={itemUp}
                    className="text-[16px] text-[#FFFFFF] font-bold"
                  >
                    حد ضرر کلی
                  </motion.span>
                  <motion.div
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span className="text-[16px] text-[#CECECE] font-medium">
                      موجودی ابتدای روز: {fmt2(dailyStart)}
                    </span>
                  </motion.div>
                  <motion.div
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span
                      dir="rtl"
                      className="text-[16px] text-[#CECECE] font-medium"
                    >
                      کمترین موجودی لحظه ای مجاز:{" "}
                      {fmt2(minOverallEquityAllowed)}$
                    </span>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: {
                      transition: { staggerChildren: 0.22, delayChildren: 0.2 },
                    },
                  }}
                  className="flex flex-col items-center justify-center gap-[9px]"
                >
                  <motion.span
                    variants={itemUp}
                    className="text-[16px] text-[#FFFFFF] font-bold"
                  >
                    حد ضرر روزانه
                  </motion.span>
                  <motion.div
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span className="text-[16px] text-[#CECECE] font-medium">
                      موجودی ابتدای روز: {fmt2(dailyStart)}
                    </span>
                  </motion.div>
                  <motion.div
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span className="text-[16px] text-[#CECECE] font-medium">
                      حداکثر حدضرر: {fmt2(dailyThresholdPct)}% از ابتدای روز
                    </span>
                  </motion.div>
                  <motion.div
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span className="text-[16px] text-[#CECECE] font-medium">
                      ضرر کل مجاز برای امروز: {fmt2(dailyMaxAllowedLoss)}
                    </span>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: {
                      transition: { staggerChildren: 0.22, delayChildren: 0.3 },
                    },
                  }}
                  className="flex flex-col items-center justify-center gap-[9px]"
                >
                  <motion.span
                    variants={itemUp}
                    className="text-[16px] text-[#FFFFFF] font-bold"
                  >
                    مشخصات کلی حساب
                  </motion.span>
                  <motion.div
                    dir="rtl"
                    variants={itemUp}
                    className="flex items-center justify-center bg-[#010016] rounded-[15px] w-[300px] h-[37px]"
                  >
                    <span className="text-[16px] text-[#CECECE] font-medium">
                      موجودی اولیه: {fmtCompactUSDNoSymbol(initialBalance)}
                    </span>
                  </motion.div>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      ) : view === "history" ? (
        <motion.div
          key="history"
          variants={viewVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          className="w-full"
        >
          <TradeHistoryComponent data={userData} target={currentTarget} />
        </motion.div>
      ) : (
        <motion.div
          key="analysis"
          variants={viewVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          className="w-full"
        >
          <TradeAnalysisComponent data={userData} target={currentTarget} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
